'use client';

import { createContext, useContext, useState, ReactNode } from 'react';
import { AuthModal } from '@/components/AuthModal';

type AuthSuccessHandler = () => void | Promise<void>;

interface AuthContextType {
  openAuthModal: (tab?: 'login' | 'register', onSuccess?: AuthSuccessHandler) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalTab, setAuthModalTab] = useState<'login' | 'register'>('login');
  const [authSuccessHandler, setAuthSuccessHandler] = useState<AuthSuccessHandler | undefined>(undefined);

  const openAuthModal = (tab: 'login' | 'register' = 'login', onSuccess?: AuthSuccessHandler) => {
    setAuthModalTab(tab);
    setAuthSuccessHandler(() => onSuccess);
    setIsAuthModalOpen(true);
  };

  return (
    <AuthContext.Provider value={{ openAuthModal }}>
      {children}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => {
          setIsAuthModalOpen(false);
          setAuthSuccessHandler(undefined);
        }}
        initialTab={authModalTab}
        onAuthSuccess={authSuccessHandler}
      />
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}